/**
 * API Routes Configuration
 * Centralized location for all API endpoint paths
 */

export const API_ROUTES = {
    // Authentication Routes
    AUTH: {
        ADMIN_LOGIN: '/auth/admin/login',
        ADMIN_LOGOUT: '/auth/logout',
        FORGOT_PASSWORD: '/auth/admin/forgot-password',
        RESET_PASSWORD: '/auth/admin/reset-password',
        REFRESH_TOKEN: '/auth/admin/refresh-token',
        VERIFY_TOKEN: '/auth/admin/verify-token',
    },

    // User Management Routes
    USERS: {
        LIST: '/admin/users',
        USER_INFO: (id: string) => `/admin/users/${id}/user-info`,
        PERSONAL_DETAIL: (id: string) => `/admin/users/${id}/personal-detail`,
        TRANSACTION_HISTORY: (id: string) =>
            `/admin/users/${id}/user-transactions-history`,
        FLAG: (id: string) => `/admin/users/${id}/flag`,
        UNFLAG: (id: string) => `/admin/users/${id}/unflag`,
    },

    // Admin Management Routes (placeholder for future use)
    ADMINS: {
        LIST: '/admins',
        DETAIL: (id: string) => `/admins/${id}`,
        CREATE: '/admins',
        UPDATE: (id: string) => `/admins/${id}`,
        DELETE: (id: string) => `/admins/${id}`,
    },

    // Transactions Routes
    TRANSACTIONS: {
        LIST: '/admin/transactions',
        DETAIL: (id: string) => `/admin/transactions/${id}`,
        STATS: '/transactions/stats',
        DOWNLOAD_RECEIPT: (id: string) =>
            `/admin/transactions/download/receipt/${id}`,
        DOWNLOAD_RECEIPTS: '/admin/transactions/download/receipts',
        DOWNLOAD_USER_HISTORY: '/admin/transactions/download/user-history',
        DOWNLOAD_USERS_HISTORY: '/admin/transactions/download/users-history',
    },

    // Transactions parked for insufficient company liquidity
    LIQUIDITY_QUEUE: {
        LIST: '/admin/liquidity/failed/list',
        RESTART: (id: string) => `/admin/liquidity/failed/${id}/restart`,
        CANCEL: (id: string) => `/admin/liquidity/failed/${id}/cancel`,
    },

    // Dashboard Routes
    DASHBOARD: {
        ADMIN: '/dashboard/admin',
        OVERVIEW: '/dashboard/overview',
        STATS: '/dashboard/stats',
    },

    // Internal Admin Management Routes
    INTERNAL_USERS: {
        LIST: '/admin/internal-users',
        DETAIL: (id: string) => `/admin/internal-users/${id}`,
        CREATE: '/admin/internal-users',
        UPDATE: (id: string) => `/admin/internal-users/${id}`,
        REVOKE_ACCESS: (id: string) =>
            `/admin/internal-users/${id}/revoke-access`,
    },

    INTERNAL_ROLES: {
        LIST: '/admin/internal-roles',
        DETAIL: (id: string) => `/admin/internal-roles/${id}`,
        CREATE: '/admin/internal-roles',
        UPDATE: (id: string) => `/admin/internal-roles/${id}`,
        DELETE: (id: string) => `/admin/internal-roles/${id}`,
        PERMISSIONS: '/admin/internal-roles/permissions/all',
    },

    // Exchange Rates / Fiat Routes
    EXCHANGE_RATES: {
        FIATS: '/admin/fiat',
        CREATE_FIAT: '/admin/fiat',
        UPDATE_FIAT: (fiatId: string) => `/admin/fiat/${fiatId}`,
    },

    // Currencies Routes
    CURRENCIES: {
        ALL: '/admin/crypto',
    },

    // Interest Rates Routes
    INTEREST_RATES: {
        LIST: '/admin/currency/interest-rates',
        DETAIL: (cryptoId: string) =>
            `/admin/currency/interest-rates/${cryptoId}`,
        UPDATE: (cryptoId: string) =>
            `/admin/currency/interest-rates/${cryptoId}`,
        BULK_UPDATE: '/admin/currency/interest-rates/bulk',
        INITIALIZE: '/admin/currency/interest-rates/initialize',
    },

    // Urgent Liquidity Routes
    // Crypto buffer (quote spread) Routes
    CRYPTO_BUFFER: {
        LIST: '/admin/crypto',
        DETAIL: (cryptoId: string) => `/admin/crypto/${cryptoId}`,
        PATCH: (cryptoId: string) => `/admin/crypto/${cryptoId}`,
        CREATE_TIER: (cryptoId: string) =>
            `/admin/crypto/${cryptoId}/buffer-tiers`,
        DELETE_TIER: (cryptoId: string, tierId: string) =>
            `/admin/crypto/${cryptoId}/buffer-tiers/${tierId}`,
    },

    URGENT_LIQUIDITY: {
        SETTINGS: '/admin/urgent-liquidity',
        REPAYMENT_RANGES: '/admin/urgent-liquidity/repayment-ranges',
        REPAYMENT_RANGES_BULK: '/admin/urgent-liquidity/repayment-ranges/bulk',
        UPDATE_REPAYMENT_RANGE: (rangeId: string) =>
            `/admin/urgent-liquidity/repayment-ranges/${rangeId}`,
        DELETE_REPAYMENT_RANGE: (rangeId: string) =>
            `/admin/urgent-liquidity/repayment-ranges/${rangeId}`,
    },

    // Auto Stacking Routes
    AUTO_STACKING: {
        SETTINGS: '/admin/auto-stacking',
        TRANSACTION_FEES: '/admin/auto-stacking/transaction-fees',
        TRANSACTION_FEES_BULK: '/admin/auto-stacking/transaction-fees/bulk',
        UPDATE_TRANSACTION_FEE: (feeId: string) =>
            `/admin/auto-stacking/transaction-fees/${feeId}`,
        DELETE_TRANSACTION_FEE: (feeId: string) =>
            `/admin/auto-stacking/transaction-fees/${feeId}`,
    },

    // Feature Flag Routes
    FEATURE_FLAGS: {
        LIST: '/admin/feature-flags',
        DETAIL: (id: string) => `/admin/feature-flags/${id}`,
        CREATE: '/admin/feature-flags',
        UPDATE: (id: string) => `/admin/feature-flags/${id}`,
        DELETE: (id: string) => `/admin/feature-flags/${id}`,
        TOGGLE: (id: string) => `/admin/feature-flags/${id}/toggle`,
        BULK_STATUS: '/admin/feature-flags/status/bulk',
        PREVIEW: (id: string) => `/admin/feature-flags/${id}/preview`,
        FIELDS: '/admin/feature-flags/fields',
    },

    FEATURE_FLAG_GROUPS: {
        LIST: '/admin/feature-flag-groups',
        DETAIL: (id: string) => `/admin/feature-flag-groups/${id}`,
        CREATE: '/admin/feature-flag-groups',
        UPDATE: (id: string) => `/admin/feature-flag-groups/${id}`,
        DELETE: (id: string) => `/admin/feature-flag-groups/${id}`,
        PREVIEW: (id: string) => `/admin/feature-flag-groups/${id}/preview`,
    },
} as const

export default API_ROUTES
